/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback, useEffect, useRef } from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { useHistory } from '../../history';
import deleteNestedKeys from '../utils/deleteNestedKeys';
import pageContainsBlobUrl from '../utils/pageContainsBlobUrl';
import { isLottieTextRowsResolution } from '../../../elements/lottie/textRows';
import {
  ELEMENT_PROPS_TO_IGNORE,
  PAGE_PROPS_TO_IGNORE,
} from '../../history/constants';
import {
  filterSavedColors,
  deleteKeysFromSavedColors,
} from '../../history/helpers';

// Record any change to core variables in history (history will know if it's a replay)
function useHistoryEntry({
  story,
  current,
  pages,
  savedColors: allSavedColors,
  selection,
  capabilities,
}) {
  const {
    state: { currentEntry },
    actions: { stateToHistory },
  } = useHistory();

  const currentHistoryEntryRef = useRef();

  const deleteKeysFromPages = useCallback((list) => {
    // Create a copy of the list not to influence the original.
    return structuredClone(list).map((page) => {
      PAGE_PROPS_TO_IGNORE.forEach((prop) => delete page[prop]);
      // If the page already exists in the CMS, then the visibility was updated with its own API call
      if (page.cmsPageId) {
        delete page.hidden;
      }
      page.elements.forEach(deleteNestedKeys(ELEMENT_PROPS_TO_IGNORE));
      return page;
    });
  }, []);

  const getEntryWithoutPropsToIgnore = useCallback(
    (entry) => {
      if (!entry) {
        return null;
      }

      const entryWithoutPropsToIgnore = structuredClone(entry);
      entryWithoutPropsToIgnore.pages = deleteKeysFromPages(entry.pages);
      entryWithoutPropsToIgnore.savedColors = deleteKeysFromSavedColors(
        entry.savedColors
      );

      return entryWithoutPropsToIgnore;
    },
    [deleteKeysFromPages]
  );

  useEffect(() => {
    if (!currentEntry) {
      return;
    }

    const entryWithoutPropsToIgnore =
      getEntryWithoutPropsToIgnore(currentEntry);

    const currentEntryWithoutPropsToIgnore = getEntryWithoutPropsToIgnore(
      currentHistoryEntryRef.current
    );

    if (
      !currentEntryWithoutPropsToIgnore ||
      JSON.stringify(entryWithoutPropsToIgnore) !==
        JSON.stringify(currentEntryWithoutPropsToIgnore)
    ) {
      currentHistoryEntryRef.current =
        getEntryWithoutPropsToIgnore(currentEntry);
    }
  }, [currentEntry, getEntryWithoutPropsToIgnore]);

  const currentPageIndexRef = useRef();
  const selectedElementIdsRef = useRef();
  useEffect(() => {
    currentPageIndexRef.current = current;
    selectedElementIdsRef.current = selection;
  }, [current, selection]);

  useEffect(() => {
    // There are some element properties that should not influence history.
    // Before adding a new history entry, let's check if the only properties that changed
    // should not influence history. Then we skip adding an entry.
    let skipAddingEntry = false;
    let shouldMergeWithLastEntry = false;
    const savedColors = filterSavedColors(allSavedColors);

    if (
      ELEMENT_PROPS_TO_IGNORE.length &&
      currentHistoryEntryRef.current &&
      pages?.length
    ) {
      const adjustedEntrySavedColors = deleteKeysFromSavedColors(savedColors);
      // If story / capabilities change, we should always add a new entry.
      const withoutPages = {
        story,
        capabilities,
        savedColors: adjustedEntrySavedColors,
      };
      const onlyPagesChanged = Object.keys(withoutPages).every(
        (key) =>
          JSON.stringify(withoutPages[key]) ===
          JSON.stringify(currentHistoryEntryRef.current[key])
      );

      // If only pages have changed, check if relevant properties have changed.
      if (onlyPagesChanged) {
        const adjustedPages = deleteKeysFromPages(pages);
        const adjustedEntryPages = deleteKeysFromPages(
          currentHistoryEntryRef.current.pages
        );
        if (isLottieTextRowsResolution(adjustedEntryPages, adjustedPages)) {
          shouldMergeWithLastEntry = true;
        }
        const pageElementWasUpdatedBySuggestion = adjustedEntryPages.some(
          (page) =>
            page.elements.some(
              (element) => element.lastUpdatedBy === 'suggestion'
            )
        );

        // When accepting a spell-check suggestion, the pages are updated twice:
        // - 1. When the suggestion is accepted and the text is updated (the lastUpdatedBy value is 'suggestion')
        // - 2. When the text box is resized based on the new text length (the lastUpdatedBy value is 'user')
        // However, we don't want these to be recorded as separate history entries, so we can ignore the first update.
        // (the second update will always happen, even if the text box is the same size, the lastUpdatedBy value will get updated anyway)
        if (pageElementWasUpdatedBySuggestion) {
          shouldMergeWithLastEntry = true;
        }
        // Check if after removing properties that shouldn't influence history, nothing changed.
        // Is so, let's skip adding a history entry.
        skipAddingEntry =
          JSON.stringify(adjustedPages) === JSON.stringify(adjustedEntryPages);
      } else {
        const { savedColors, ...withoutSavedColors } = withoutPages;
        const onlySavedColorsChanged = Object.keys(withoutSavedColors).every(
          (key) =>
            JSON.stringify(withoutSavedColors[key]) ===
            JSON.stringify(currentHistoryEntryRef.current[key])
        );

        if (onlySavedColorsChanged) {
          // If saved colors was previously empty and isn't, but all colours have been synced,
          // they're coming from the API initial load and we can ignore this history entry

          if (
            currentHistoryEntryRef.current.savedColors?.length === 0 &&
            savedColors.length > 0 &&
            savedColors.every((savedColor) => !savedColor.isLocal)
          ) {
            shouldMergeWithLastEntry = true;
          }
        }

        if (
          onlySavedColorsChanged &&
          savedColors.length ===
            currentHistoryEntryRef.current.savedColors.length
        ) {
          // If the saved color was being edited, and has now been saved, we can ignore all editing entries
          // (if the user was dragging their cursor around the color wheel, there'd be a lot of entries)
          let onlyShouldSyncPropChanged = true;

          for (const savedColor of savedColors) {
            const { shouldSync, ...withoutShouldSync } = savedColor;
            const previousSavedColor =
              currentHistoryEntryRef.current.savedColors.find(
                (color) => color.color.id === savedColor.color.id
              );
            if (!previousSavedColor) {
              onlyShouldSyncPropChanged = false;
              break;
            }
            const {
              shouldSync: previousShouldSync,
              ...previousWithoutShouldSync
            } = previousSavedColor;

            if (
              JSON.stringify(withoutShouldSync) !==
              JSON.stringify(previousWithoutShouldSync)
            ) {
              onlyShouldSyncPropChanged = false;
              break;
            }
          }

          if (onlyShouldSyncPropChanged) {
            shouldMergeWithLastEntry = true;
          }
        }
      }

      if (pageContainsBlobUrl(pages)) {
        skipAddingEntry = true;
      }
    }

    if (!skipAddingEntry) {
      // If story / capabilities change, we should always add a new entry.

      stateToHistory(
        {
          story,
          current: currentPageIndexRef.current,
          selection: selectedElementIdsRef.current,
          pages,
          capabilities,
          savedColors,
        },
        shouldMergeWithLastEntry
      );
    }
  }, [
    allSavedColors,
    story,
    pages,
    stateToHistory,
    capabilities,
    deleteKeysFromPages,
  ]);
}

export default useHistoryEntry;
